import React, { useRef, useEffect, useState } from 'react';
import { Group, Rect, Text as KonvaText } from 'react-konva';
import Konva from 'konva';
import { observer } from 'mobx-react-lite';

const TextElementComponent = observer(({ element, isSelected, onSelect, onTransformEnd, onDragEnd, groupChild }) => {
  const groupRef = useRef(null);
  const textRef = useRef(null);
  const [textHeight, setTextHeight] = useState(element.height || 50);

  // Force Konva to recalculate text layout when fonts finish loading.
  useEffect(() => {
    const onFontLoaded = () => {
      const node = textRef.current;
      if (!node) return;
      const f = node.fontFamily();
      node.fontFamily('serif');
      node.fontFamily(f);
      const h = node.height();
      if (h > 0) {
        setTextHeight(h);
        if (Math.abs(h - element.height) > 1) element.set({ height: h });
      }
      // Re-cache if blur is active
      if (element.blurEnabled && node) {
        node.cache();
      }
      node.getLayer()?.batchDraw();
    };
    document.fonts.addEventListener('loadingdone', onFontLoaded);
    return () => document.fonts.removeEventListener('loadingdone', onFontLoaded);
  }, []);

  // Sync Konva auto-sized height back to element model after text reflows
  useEffect(() => {
    const node = textRef.current;
    if (!node) return;
    const frame = requestAnimationFrame(() => {
      const h = node.height();
      if (h > 0) {
        setTextHeight(h);
        if (Math.abs(h - element.height) > 1) element.set({ height: h });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [element.text, element.fontSize, element.fontFamily, element.fontWeight, element.width]);

  // Apply blur filter: cache the text node when blur is enabled
  useEffect(() => {
    const node = textRef.current;
    if (!node) return;
    if (element.blurEnabled) {
      node.filters([Konva.Filters.Blur]);
      node.blurRadius(element.blurRadius);
      node.cache();
    } else {
      node.filters([]);
      node.clearCache();
    }
    node.getLayer()?.batchDraw();
  }, [element.blurEnabled, element.blurRadius, element.text, element.fontSize, element.width]);

  if (!element.visible) return null;

  const handleDragEnd = (e) => {
    element.set({
      x: e.target.x(),
      y: e.target.y(),
    });
    if (onDragEnd) onDragEnd(e);
  };

  // During live drag: let Konva scale the node visually — no imperative changes,
  // no element.set(). Any MobX update here causes React to sync stale x/y back
  // to the node every frame, fighting the Transformer (especially top/left anchors).
  const handleTransform = () => {};

  const handleTransformEnd = (e) => {
    const node = e.target;
    // Read accumulated scale against the MODEL values (not node.width/height,
    // which accumulate across multiple transform sessions and can drift).
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const newWidth = Math.max(20, element.width * Math.abs(scaleX));
    const newFontSize = Math.max(8, Math.round(element.fontSize * Math.abs(scaleY)));

    const tNode = textRef.current;
    if (tNode) {
      tNode.width(newWidth);
      tNode.fontSize(newFontSize);
    }

    node.scaleX(1);
    node.scaleY(1);
    node.width(newWidth);

    element.set({
      x: node.x(),
      y: node.y(),
      width: newWidth,
      fontSize: newFontSize,
      rotation: node.rotation(),
    });

    if (onTransformEnd) onTransformEnd(e);
  };

  const handleDblClick = () => {
    if (!element.contentEditable) return;
    element.toggleEditMode(true);

    const textNode = textRef.current;
    if (!textNode) return;

    const grp = groupRef.current;
    const stage = textNode.getStage();
    if (!stage) return;

    // Hide the group so the textarea appears as in-place editing
    if (grp) grp.hide();
    stage.batchDraw();

    const stageContainer = stage.container();
    const textPosition = textNode.absolutePosition();
    const absScale = textNode.getAbsoluteScale();
    const scaleX = absScale.x;
    const scaleY = absScale.y;

    const cssFontStyle = element.fontStyle === 'italic' ? 'italic' : 'normal';
    const cssFontWeight = String(element.fontWeight || 'normal');

    const originalText = element.text;

    const textarea = document.createElement('textarea');
    textarea.value = '';
    textarea.placeholder = originalText;
    textarea.style.position = 'absolute';
    textarea.style.top = `${textPosition.y}px`;
    textarea.style.left = `${textPosition.x}px`;
    textarea.style.width = `${element.width * scaleX}px`;
    textarea.style.height = `${element.height * scaleY}px`;
    textarea.style.fontSize = `${element.fontSize * scaleX}px`;
    textarea.style.fontFamily = `"${element.fontFamily}", sans-serif`;
    textarea.style.fontStyle = cssFontStyle;
    textarea.style.fontWeight = cssFontWeight;
    textarea.style.textAlign = element.align;
    textarea.style.color = element.fill;
    textarea.style.border = '0';
    textarea.style.borderWidth = '0';
    textarea.style.padding = '0';
    textarea.style.margin = '0';
    textarea.style.overflow = 'hidden';
    textarea.style.background = 'none';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.appearance = 'none';
    textarea.style.webkitAppearance = 'none';
    textarea.style.lineHeight = String(
      typeof element.lineHeight === 'number'
        ? element.lineHeight
        : parseFloat(element.lineHeight) || 1.2
    );
    textarea.style.letterSpacing = `${(element.letterSpacing || 0) * scaleX}px`;
    textarea.style.transformOrigin = 'left top';
    textarea.style.transform = `rotate(${element.rotation}deg)`;
    textarea.style.textDecoration = element.textDecoration || '';
    textarea.style.boxSizing = 'content-box';
    textarea.style.zIndex = '10000';
    textarea.style.whiteSpace = 'pre-wrap';
    textarea.style.wordBreak = 'break-word';
    textarea.style.minHeight = '0';
    textarea.style.minWidth = '0';

    // Placeholder inherits the element's text color at 40% opacity
    const placeholderStyle = document.createElement('style');
    placeholderStyle.textContent = `
      textarea:placeholder-shown { opacity: 1; }
      textarea::placeholder { color: ${element.fill}; opacity: 0.4; }
    `;
    stageContainer.appendChild(placeholderStyle);

    stageContainer.appendChild(textarea);
    textarea.focus();

    let removed = false;
    const removeTextarea = (cancel = false) => {
      if (removed) return;
      removed = true;
      const newText = !cancel && textarea.value.length > 0 ? textarea.value : originalText;
      element.set({ text: newText });
      element.toggleEditMode(false);
      if (grp) grp.show();
      stage.batchDraw();
      if (textarea.parentNode) {
        textarea.parentNode.removeChild(textarea);
      }
      if (placeholderStyle.parentNode) {
        placeholderStyle.parentNode.removeChild(placeholderStyle);
      }
    };

    textarea.addEventListener('blur', () => removeTextarea(false));
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') removeTextarea(true);
    });
  };

  // Compute displayed text based on textTransform
  let displayText = element.text || element.placeholder || '';
  if (element.textTransform === 'uppercase') {
    displayText = displayText.toUpperCase();
  } else if (element.textTransform === 'lowercase') {
    displayText = displayText.toLowerCase();
  } else if (element.textTransform === 'capitalize') {
    displayText = displayText.replace(/\b\w/g, (c) => c.toUpperCase());
  }

  const fontStyleStr = (() => {
    const parts = [];
    const style = String(element.fontStyle || '').trim();
    const weight = String(element.fontWeight || '').trim();
    if (style === 'italic' || style.includes('italic')) parts.push('italic');
    if (weight && weight !== 'normal') parts.push(weight);
    return parts.length > 0 ? parts.join(' ') : 'normal';
  })();

  const pad = element.backgroundEnabled ? (element.backgroundPadding || 0) : 0;

  return (
    <Group
      ref={groupRef}
      id={element.id}
      name={element.name || 'text'}
      x={element.x}
      y={element.y}
      width={element.width}
      height={textHeight}
      rotation={element.rotation}
      opacity={element.opacity}
      draggable={!groupChild && element.draggable}
      visible={element.visible}
      onClick={(e) => { if (!groupChild && onSelect) onSelect(e); }}
      onTap={(e) => { if (!groupChild && onSelect) onSelect(e); }}
      onDblClick={handleDblClick}
      onDblTap={handleDblClick}
      onDragEnd={handleDragEnd}
      onTransform={handleTransform}
      onTransformEnd={handleTransformEnd}
    >
      {/* Background rect */}
      {element.backgroundEnabled && (
        <Rect
          x={-pad}
          y={-pad}
          width={element.width + pad * 2}
          height={textHeight + pad * 2}
          fill={element.backgroundColor || '#ffffff'}
          opacity={element.backgroundOpacity ?? 1}
          cornerRadius={element.backgroundCornerRadius || 0}
          listening={false}
        />
      )}

      {/* Text */}
      <KonvaText
        ref={textRef}
        text={displayText}
        width={element.width}
        fontSize={element.fontSize}
        fontFamily={element.fontFamily}
        fontStyle={fontStyleStr}
        textDecoration={element.textDecoration}
        fill={element.fill}
        align={element.align}
        verticalAlign={element.verticalAlign}
        lineHeight={typeof element.lineHeight === 'number' ? element.lineHeight : parseFloat(element.lineHeight) || 1.2}
        letterSpacing={element.letterSpacing}
        stroke={element.stroke || undefined}
        strokeWidth={element.strokeWidth || 0}
        fillAfterStrokeEnabled={true}
        shadowEnabled={element.shadowEnabled}
        shadowColor={element.shadowColor}
        shadowBlur={element.shadowBlur}
        shadowOffsetX={element.shadowOffsetX}
        shadowOffsetY={element.shadowOffsetY}
        shadowOpacity={element.shadowOpacity}
      />
    </Group>
  );
});

TextElementComponent.displayName = 'TextElement';
export default TextElementComponent;
